import { debounceTime, distinctUntilChanged } from "rxjs/operators";
import {
  Component,
  Injector,
  NgZone,
  OnDestroy,
  OnInit,
  afterNextRender,
  inject,
  viewChildren,
  viewChild,
  ChangeDetectionStrategy,
} from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { SelectItem } from "primeng/api";
import { BehaviorSubject, Subject } from "rxjs";
import { ReplaySubject, Subscription } from "rxjs";
import { incotermsList } from "../../shared/incoterms/incoterms.const";
import { FclQuote } from "../models/quote-fcl.model";
import { ManualQuote } from "../models/quote-manual.model";
import { quoteLocationType } from "./quote-chips/quote-location-type.const";
import { QuoteChipsComponent } from "./quote-chips/quote-chips.component";
import { QuoteMailEditorComponent } from "../quote-mail-editor/quote-mail-editor.component";
import { QuoteManualEditorComponent } from "../quote-manual-editor/quote-manual-editor.component";
import { QuotePreviewComponent } from "../quote-preview/quote-preview.component";
import { QuoteFreightRateComponent } from "../quote-freight-rate/quote-freight-rate.component";
import { QuoteTermsComponent } from "../quote-terms/quote-terms.component";
import { QuoteRepository } from "../quote.repository";
import { Quote } from "../quote.type";
import { quoteFormTabs } from "./quote-form-tabs.const";
import { directionForIncoterm, quoteDirection } from "./quote-direction.const";
import { quoteStatus } from "./quote-status.const";
import { quoteType } from "./quote-type.const";
import { addDays, differenceInCalendarDays, format } from "date-fns";
import { QuoteMapper } from "../models/quote.mapper";
import { FclTariff } from "../models/quote-fcl-tariff.model";
import { QuoteLocation } from "../models/quote-location.model";
import { Client } from "../../client/client.model";
import { SentryErrorHandler } from "../../shared/error-handler/sentry-error-handler";
import { QuoteSurchargeComponent } from "../quote-surcharge/quote-surcharge.component";
import { QuoteContractListComponent } from "../quote-contract-list/quote-contract-list.component";
import { containerLoad } from "../../shared/container/load-types.const";
import { User } from "../../user/user.model";
import { role } from "../../user/role.const";
import { NotifyService } from "../../notify/notify.service";
import { UserService } from "../../user/user.service";
import { noop } from "../../shared/noop.function";
import { TOAST } from "../../notify/toast/toast-type.enum";
import { DateRange } from "../../shared/date/date-range.model";
import { eventType } from "../../notify/event.const";
import { BackendErrorPipe } from "../../shared/backend/backend-error.pipe";
import { locationKind } from "../../location/location-kind.const";
import { LocationKindService } from "../../location/location-kind.service";
import { QuoteUpdateService } from "./quote-update.service";
import { QuoteHandoverService } from "./quote-handover.service";
import { BaseStandardQuote } from "../models/quote-base-standard.model";
import { QuoteEmail } from "../models/email.model";
import { SortEquipmentPipe } from "../../shared/container/sort-equipment.pipe";
import { EventService } from "../../shared/event/event.service";
import { LtlQuote } from "../models/quote-ltl.model";
import { BaseLclQuote } from "../models/quote-base-lcl.model";
import { QuoteLtlSurchargeComponent } from "../quote-ltl-surcharge/quote-ltl-surcharge.component";
import { Rate } from "../../rate/rate-type";
import { FtlQuote } from "../models/quote-ftl.model";
import { PtlQuote } from "../models/quote-ptl.model";
import { EmailQuote } from "../models/quote-email.model";
import { quoteSource } from "./quote-source.const";
import { EquipmentQuantityService } from "../quote-equipment/equipment-quantity.service";
import { AirTariffQuote } from "../models/quote-airtariff.model";
import { AirQuote } from "../models/quote-air.model";
import { module } from "app/user/module.const";
import { reasonOfDecline } from "../quote-decline-form/reason-of-decline.const";
import { MultiQuote } from "../models/quote-multi.model";
import { BrokerageQuote } from "../models/quote-brokerage.model";
import { QuoteVariantSelectComponent } from "../quote-variant-select/quote-variant-select.component";
import { Bind } from "primeng/bind";
import { ButtonDirective } from "primeng/button";
import { InputText } from "primeng/inputtext";
import { Checkbox } from "primeng/checkbox";
import { Panel } from "primeng/panel";
import { Tabs, TabList, Tab, TabPanels } from "primeng/tabs";
import { ClientSelectComponent } from "../../ui/dropdown/client-select/client-select.component";
import { DatePickerComponent } from "../../ui/date-picker/date-picker.component";
import { Tooltip } from "primeng/tooltip";
import { DatePicker } from "primeng/datepicker";
import { FormsModule } from "@angular/forms";
import { Select } from "primeng/select";
import { SelectButton } from "primeng/selectbutton";
import { AirWeightBracketSelectComponent } from "../../ui/dropdown/air-weight-bracket-select/air-weight-bracket-select.component";
import { ContainerSelectComponent } from "../../ui/dropdown/container-select/container-select.component";
import { ServiceCodeSelectComponent } from "../../ui/dropdown/service-code-select/service-code-select.component";
import { QuotePackageComponent } from "./quote-package/quote-package.component";
import { Package } from "./quote-package/package.model";
import { QuoteInsuranceComponent } from "./quote-insurance/quote-insurance.component";
import { QuoteChipsSingleComponent } from "./quote-chips-single/quote-chips-single.component";
import { QuoteChipsGroupComponent } from "./quote-chips-group/quote-chips-group.component";
import { SpinnerComponent } from "../../ui/spinner/spinner.component";
import { WysiwygComponent } from "../../ui/wysiwyg/wysiwyg.component";
import { QuoteOptionsComponent } from "../quote-options/quote-options.component";
import { QuoteFormActionsComponent } from "./quote-form-actions/quote-form-actions.component";
import { ClientPotentialFormComponent } from "../../client/client-potential-form/client-potential-form.component";
import { TimeAgoPipe } from "../../shared/time-ago/time-ago.pipe";
import { QuoteAiComponent } from "./quote-ai/quote-ai.component";
import { QuoteEmailPreviewComponent } from "./quote-email-preview/quote-email-preview.component";

@Component({
  selector: "app-quote-form",
  templateUrl: "./quote-form.component.html",
  styleUrls: ["./quote-form.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ButtonDirective,
    QuoteVariantSelectComponent,
    Bind,
    InputText,
    Panel,
    Tabs,
    TabList,
    Tab,
    TabPanels,
    ClientSelectComponent,
    DatePickerComponent,
    Tooltip,
    DatePicker,
    FormsModule,
    Select,
    SelectButton,
    AirWeightBracketSelectComponent,
    ContainerSelectComponent,
    ServiceCodeSelectComponent,
    QuotePackageComponent,
    QuoteInsuranceComponent,
    QuoteChipsComponent,
    QuoteChipsSingleComponent,
    QuoteChipsGroupComponent,
    SpinnerComponent,
    QuoteManualEditorComponent,
    QuoteContractListComponent,
    QuoteFreightRateComponent,
    QuoteLtlSurchargeComponent,
    WysiwygComponent,
    QuoteSurchargeComponent,
    QuoteOptionsComponent,
    QuoteTermsComponent,
    QuotePreviewComponent,
    QuoteMailEditorComponent,
    QuoteFormActionsComponent,
    ClientPotentialFormComponent,
    TimeAgoPipe,
    QuoteAiComponent,
    QuoteEmailPreviewComponent,
    Checkbox,
  ],
})
export class QuoteFormComponent implements OnInit, OnDestroy {
  private quoteRepository = inject(QuoteRepository);
  private notifyService = inject(NotifyService);
  private eventService = inject(EventService);
  private userService = inject(UserService);
  private locationKindService = inject(LocationKindService);
  private quoteUpdateService = inject(QuoteUpdateService);
  private router = inject(Router);
  private quoteHandover = inject(QuoteHandoverService);
  private route = inject(ActivatedRoute);
  private equipmentQuantityService = inject(EquipmentQuantityService);
  private injector = inject(Injector);
  private ngZone = inject(NgZone);

  public readonly positiveNumberPattern = "(?=.*[1-9])([0-9]*[.,])?[0-9]+";

  public readonly SurchargeList = viewChildren(QuoteSurchargeComponent);
  public readonly FreightRateList = viewChildren(QuoteFreightRateComponent);
  public readonly OriginLocationChips = viewChild<QuoteChipsComponent>(
    "OriginLocationChips",
  );
  public readonly DestinationLocationChips = viewChild<QuoteChipsComponent>(
    "DestinationLocationChips",
  );
  public readonly QuoteTabs = viewChild(Tabs);
  public readonly QuoteTerms = viewChild<QuoteTermsComponent>("QuoteTerms");
  public readonly MailEditor =
    viewChild<QuoteMailEditorComponent>("MailEditor");
  public readonly Preview = viewChild<QuotePreviewComponent>("Preview");
  public readonly ManualEditor =
    viewChild<QuoteManualEditorComponent>("ManualEditor");
  public readonly TariffContractList =
    viewChild<QuoteContractListComponent>("TariffContractList");
  public readonly SurchargeTypeMultiselect =
    viewChild<QuoteLtlSurchargeComponent>("SurchargeTypeMultiselect");

  public quote: Quote = QuoteMapper.map({
    discriminator: containerLoad.fcl,
  });

  public user: User = null;
  public role = role;
  public tabs = quoteFormTabs;
  public chip = quoteLocationType;
  public quoteType = quoteType;
  public quoteDirection = quoteDirection;
  public quoteStatus = quoteStatus;
  public incotermsListConst = incotermsList;
  public incotermsList: SelectItem[] = [];
  public directionList: SelectItem[] = [];
  public quoteTypeList: SelectItem[] = [];
  public selectedQuoteType = "";
  public selectedDirection = "";
  public selectedIncotermType = "";
  public isMailFormValid = false;
  public haveRoute: boolean[] = [];
  public routeCount = 0;
  public selectedRates: Rate[] = null;
  public selectedRange: DateRange;
  public deepLink: string;
  private ptlPickupRange: DateRange = null;
  private ptlDeliveryRange: DateRange = null;
  public loading = false;
  public locationKindFilter = "";
  public locationKindFilterOptions: string[] = [];
  public activeTab: string;
  public internalCommentSubject = new Subject<string>();
  public termsSubject = new ReplaySubject<string>(2);
  public internalComment: string;
  public showOfferComment = false;
  public showInternalNotes = false;
  public tabBehavior = new BehaviorSubject<string>("");
  public subscription = new Subscription();
  public today = new Date(new Date().setHours(0, 0, 0, 0));
  private readonly defaultValidFromTime = "00:00";
  private readonly defaultValidToTime = "23:59";
  public marginUpdateSubject = new Subject<void>();
  public potentialPopupVisible = false;
  private sentry = inject(SentryErrorHandler);
  private shouldApplyPtlValidityOnCreate = false;
  public reasonOfDecline = reasonOfDecline;
  public childQuotes: Quote[] = [];

  ngOnInit() {
    this.shouldApplyPtlValidityOnCreate =
      this.route.snapshot.params["id"] === "0";
    this.setQuoteFromResolve();
    this.watchEventsToRefreshQuote();
    this.watchEventsToRefreshMargin();
    this.setLocationKindFilterFromQuoteLocationsOrSelectedQuoteType();
    this.setLocationKindFilterOptions();
    this.addInitSubscriptions();
    this.setUser();
    this.observeTermsChanges();

    // TODO: These should be 4 separate components
    this.buildIncotermsList();
    this.buildDirectionList();
    this.buildQuoteTypeList();

    this.refreshQuoteDetails();
    this.scrollTop();
  }

  public ngOnDestroy() {
    this.removeEmptyQuote();
    this.subscription.unsubscribe();
  }

  public close() {
    if (window.history.length > 1) {
      window.history.back();
    } else {
      this.router.navigate(["/quote/list"]);
    }
  }

  public checkQuotePotential() {
    if (
      this.quote instanceof FclTariff ||
      this.quote instanceof ManualQuote ||
      this.quote instanceof MultiQuote ||
      this.quote instanceof EmailQuote ||
      this.quote instanceof FtlQuote ||
      this.quote instanceof PtlQuote
    ) {
      this.goToQuoteList();

      return;
    }

    this.subscription.add(
      this.quoteRepository.getPotential(this.quote.getId()).subscribe({
        next: (potentialExists) => {
          if (
            !potentialExists &&
            this.user.getModules()?.indexOf(module.crm) !== -1 &&
            this.quote instanceof BaseStandardQuote &&
            this.quote.getClients().length === 1 &&
            this.quote.getHasOrigin() &&
            this.quote.getHasDestination() &&
            this.quote.getOriginLocations().length == 1 &&
            this.quote.getDestinationLocations().length == 1
          ) {
            this.potentialPopupVisible = true;
          } else {
            this.goToQuoteList();
          }
        },
        error: (err) => {
          this.notifyService.toast(TOAST.ERROR, "Error");
        },
      }),
    );
  }

  public goToQuoteList() {
    this.router.navigate(["/quote/list"]);
  }

  public setQuote(quote: Quote) {
    this.quote = quote;
    this.selectedRange = new DateRange(
      this.withValidityTime(this.quote.getValidFrom(), this.getValidFromTime()),
      this.withValidityTime(this.quote.getValidTo(), this.getValidToTime()),
    );
  }

  public redirectToNewQuote(quote: Quote) {
    // Swapping the url through the router (and not history.replaceState) keeps the router's own
    // url in sync - anything navigating relatively afterwards, grids included, would otherwise
    // resolve against the stale `quote/0`. The handover keeps the resolver from fetching it again.
    if (this.route.snapshot.params["id"] !== String(quote.getId())) {
      const commands = quote.getDisplayId()
        ? ["/quote", quote.getId(), quote.getDisplayId()]
        : ["/quote", quote.getId()];
      this.quoteHandover.hand(quote);
      this.router.navigate(commands, { replaceUrl: true });
    }
    this.setQuote(quote);
    this.refreshQuoteDetails();
    this.childQuotes = [];
    this.fetchChildQuotes();
  }

  public onQuoteReplicated(quote: Quote) {
    this.redirectToNewQuote(quote);
    this.showFirstTab();
  }

  // The variant dropdown is fed from the quote list endpoint, which serializes
  // fewer fields than the detail one - refetch before putting it in the form.
  public onQuoteVariantChange(quote: Quote): void {
    this.subscription.add(
      this.quoteRepository.getQuote(quote.getId()).subscribe({
        next: (variantQuote) => {
          this.selectedRates = null;
          this.deepLink = null;
          this.haveRoute = [];
          this.redirectToNewQuote(variantQuote);
          this.setLocationKindFilterFromQuoteLocationsOrSelectedQuoteType();
          this.setLocationKindFilterOptions();
          this.showFirstTab();
          this.updateSharedComponents();
          this.scrollTop();
        },
        error: (err) => {
          this.sentry.handleError(err, { quoteVariantId: quote.getId() });
          this.notifyService.toast(TOAST.ERROR, "Error");
        },
      }),
    );
  }

  public isQuoteFormBlocked(): boolean {
    return (
      (this.quote && this.user && this.user.hasRole(role.readOnly)) ||
      this.quote.getStatus() !== quoteStatus.draft
    );
  }

  /**
   * Deliberately not gated on isQuoteFormBlocked(): removing a file that turned out to hold a virus
   * or something illegal has to stay possible on a quote that has already been sent. The server
   * enforces QUOTE_EDIT on the parent quote; this only hides the button from users who plainly
   * cannot use it.
   */
  public canDeleteAttachments(): boolean {
    return Boolean(this.user) && !this.user.hasRole(role.readOnly);
  }

  public isQuoteSourceOnline(): boolean {
    return this.quote.getSource() === quoteSource.online;
  }

  // An online PTL quote keeps only its account and legs locked; every other
  // online quote has its whole header locked.
  public isOnlineQuoteHeaderBlocked(): boolean {
    return this.isQuoteSourceOnline() && !this.isPtlQuote();
  }

  public isQuoteTypeExemptFromBlock(): boolean {
    return this.isQuoteSourceOnline() && !this.isQuoteFormBlocked();
  }

  public areQuoteChipsBlocked(): boolean {
    return this.isQuoteFormBlocked() || !this.isFirstTabActive();
  }

  public onQuoteAi(pending: Quote): any {
    this.quote = pending;
    this.refreshQuoteDetails();
  }

  // The incoterm decides whether the quote is an import or an export; the direction
  // selector stays free afterwards, so the user can still move it to crosstrade.
  public selectIncoterm(incotermType: string): void {
    const direction = directionForIncoterm(incotermType);
    if (!direction || direction === this.selectedDirection) {
      this.updateQuote({ incotermType: incotermType });

      return;
    }

    this.selectedDirection = direction;
    this.updateQuote({ incotermType: incotermType, direction: direction });
  }

  public updateQuote(pending: any): any {
    if (!this.user || this.user.hasRole(this.role.readOnly)) {
      return;
    }

    this.quote = this.quoteUpdateService.getUpdatedQuoteFromPending(
      this.quote,
      pending,
    );
    const payload = this.quoteUpdateService.getPayloadFromQuoteAndPending(
      this.quote,
      pending,
    );

    this.emitQuoteUpdating();
    this.subscription.add(
      this.quoteRepository.patchQuote(this.quote.getId(), payload).subscribe({
        next: (updatedQuote) => {
          this.notifyService.toast(TOAST.SUCCESS, "Quote has been updated");
          this.handleQuoteUpdate(updatedQuote, pending);
          this.emitQuoteUpdated();
        },
        error: (err) => {
          // No `quoteUpdated` follows a failed save; without this the header
          // and the freight tab would wait for it forever.
          this.loading = false;
          this.sentry.handleError(err, { updateQuotePayload: payload });
          this.notifyService.toast(
            TOAST.ERROR,
            "Quote has not been updated",
            <string>new BackendErrorPipe().transform(err, "string"),
          );
        },
      }),
    );

    return payload;
  }

  public selectDate(dates: any): void {
    if (this.quote instanceof PtlQuote) {
      this.selectPtlValidity(this.quote, dates.start, dates.end);

      return;
    }

    this.updateQuote(new DateRange(dates.start, dates.end));
  }

  public getLclAndAirWeight() {
    if (this.quote instanceof BaseLclQuote) {
      return isNaN(this.quote.getWeight()) ? null : this.quote.getWeight();
    }
  }

  public getLclAndAirMeasurement() {
    if (this.quote instanceof BaseLclQuote) {
      return isNaN(this.quote.getMeasurement())
        ? null
        : this.quote.getMeasurement();
    }
  }

  public getLdm() {
    if (this.quote instanceof BaseLclQuote) {
      return !isNaN(this.quote.getLdm()) ? this.quote.getLdm() : null;
    }
  }

  public getIntendedPickupDate() {
    if (this.quote instanceof LtlQuote || this.quote instanceof PtlQuote) {
      return this.quote.getIntendedPickupDate();
    }
  }

  public getLclAndAirPieces() {
    if (this.quote instanceof BaseLclQuote) {
      return !isNaN(this.quote.getPieces()) ? this.quote.getPieces() : null;
    }
  }

  public isEquipmentValid(): boolean {
    if (this.quote instanceof MultiQuote) {
      const hasEquipment =
        this.quote.getEquipment() && this.quote.getEquipment().length > 0;

      return hasEquipment || this.hasCargoFigures();
    }

    return true;
  }

  // A multi-quote can be priced off cargo figures instead, so equipment only
  // becomes mandatory there once weight and measurement are missing.
  public isEquipmentRequired(): boolean {
    if (this.quote instanceof MultiQuote) {
      return !this.hasCargoFigures();
    }

    return this.isFclQuoteOrFclTariff() && !this.isBrokerageQuote();
  }

  private hasEquipment(): boolean {
    if (
      this.quote instanceof FclQuote ||
      this.quote instanceof FclTariff ||
      this.quote instanceof MultiQuote
    ) {
      return !!this.quote.getEquipment()?.length;
    }

    return false;
  }

  private hasCargoFigures(): boolean {
    if (!(this.quote instanceof MultiQuote)) {
      return false;
    }

    const hasWeight =
      !isNaN(this.quote.getWeight()) && this.quote.getWeight() > 0;
    const hasMeasurement =
      !isNaN(this.quote.getMeasurement()) && this.quote.getMeasurement() > 0;

    return hasWeight && hasMeasurement;
  }

  // A multi-quote priced off equipment carries no cargo figures of its own,
  // so weight and the rest stop being required once equipment is chosen.
  private areCargoFiguresRequired(): boolean {
    if (this.quote instanceof MultiQuote) {
      return !this.quote.getEquipment()?.length;
    }

    return true;
  }

  public isWeightRequired(): boolean {
    return this.areCargoFiguresRequired();
  }

  // A cargo figure the quote type does not ask for may legitimately be empty or
  // zero, so the positive-number constraint only applies where it is required.
  public getMeasurementPattern(): string | null {
    return this.isMeasurementRequired() ? this.positiveNumberPattern : null;
  }

  public getPiecesMin(): number {
    return this.isPiecesRequired() ? 1 : 0;
  }

  public isMeasurementRequired(): boolean {
    return this.areCargoFiguresRequired() && !this.isPtlQuote();
  }

  public isLdmRequired(): boolean {
    return this.areCargoFiguresRequired() && this.isPtlQuote();
  }

  public isPiecesRequired(): boolean {
    return this.areCargoFiguresRequired() && !this.isPtlQuote();
  }

  public readonly measuresLockedTooltip =
    "Totals are calculated from package details. Edit packages to update them. To fill total shipment manually, delete all packages.";

  // Packages own the cargo figures while they exist: the header shows their
  // totals locked, so a manual edit cannot silently drop them.
  public hasPackages(): boolean {
    return this.getPackages().length > 0;
  }

  // An online quote's packages are read-only, so a manual measure stays its
  // only edit path.
  public areMeasuresLockedByPackages(): boolean {
    return this.hasPackages() && !this.isQuoteSourceOnline();
  }

  public getPackageTypesLabel(): string {
    const count = this.getPackages().length;

    return `${count} package ${count === 1 ? "type" : "types"}`;
  }

  public getPalletCount(): number {
    return this.getPackages()
      .filter((pkg) => pkg.pallet)
      .reduce((sum, pkg) => sum + pkg.quantity, 0);
  }

  public getPalletsLabel(): string {
    const count = this.getPalletCount();

    return `${count} ${count === 1 ? "pallet" : "pallets"}`;
  }

  public getStackabilityLabel(): string {
    const packages = this.getPackages();

    if (packages.every((pkg) => pkg.stackable)) {
      return "stackable";
    }

    if (packages.every((pkg) => !pkg.stackable)) {
      return "non-stackable";
    }

    return "mixed stackability";
  }

  private getPackages(): Package[] {
    if (this.quote instanceof BaseLclQuote) {
      return this.quote.getPackages() ?? [];
    }

    return [];
  }

  public isNotNullAndGreaterThanZero(value): boolean {
    if (this.quote instanceof MultiQuote) {
      return (
        (this.quote.getEquipment() && this.quote.getEquipment().length > 0) ||
        (!isNaN(value) && value > 0)
      );
    } else {
      return !isNaN(value) && value > 0;
    }
  }

  public isInt(value): boolean {
    return value % 1 === 0;
  }

  public onWeightChange(event) {
    const strValue = event.target.value.replace(",", ".");
    let weight = parseFloat(strValue);
    if (isNaN(weight)) {
      weight = 0;
    }
    if (this.quote instanceof BaseLclQuote) {
      this.quote.setWeight(weight);
      this.quote.setPackages([]);
      this.updateQuote({ weight: weight, packages: [] });
    }
    if (this.quote instanceof MultiQuote) {
      this.childQuotes = [];
    }
  }

  public onMeasurementChange(event) {
    const strValue = event.target.value.replace(",", ".");
    let measurement = parseFloat(strValue);
    if (isNaN(measurement)) {
      measurement = 0;
    }
    if (this.quote instanceof BaseLclQuote) {
      this.quote.setMeasurement(measurement);
      this.quote.setPackages([]);
      this.updateQuote({ measurement: measurement, packages: [] });
    }
    if (this.quote instanceof MultiQuote) {
      this.childQuotes = [];
    }
  }

  public onLdmChange(event) {
    const strValue = event.target.value.replace(",", ".");
    const ldm = parseFloat(strValue);
    if (this.quote instanceof BaseLclQuote) {
      this.quote.setLdm(ldm);
      this.quote.setPackages([]);
      this.updateQuote({ ldm: ldm, packages: [] });
    }
  }

  public onIntendedPickupDateChange(event) {
    if (this.quote instanceof LtlQuote || this.quote instanceof PtlQuote) {
      this.quote.setIntendedPickupDate(event);
      this.updateQuote({ intendedPickupDate: event });
    }
  }

  public getPtlPickupRange(): DateRange {
    if (!(this.quote instanceof PtlQuote)) {
      return null;
    }

    this.ptlPickupRange = this.buildStableRange(
      this.ptlPickupRange,
      this.withRangeStartTime(
        this.quote.getIntendedPickupDate(),
        this.quote.getIntendedPickupTimeFrom(),
      ),
      this.withRangeEndTime(
        this.quote.getIntendedPickupDateTo(),
        this.quote.getIntendedPickupTimeTo(),
      ),
    );

    return this.ptlPickupRange;
  }

  public getPtlDeliveryRange(): DateRange {
    if (!(this.quote instanceof PtlQuote)) {
      return null;
    }

    this.ptlDeliveryRange = this.buildStableRange(
      this.ptlDeliveryRange,
      this.withRangeStartTime(
        this.quote.getIntendedDeliveryDateFrom(),
        this.quote.getIntendedDeliveryTimeFrom(),
      ),
      this.withRangeEndTime(
        this.quote.getIntendedDeliveryDateTo(),
        this.quote.getIntendedDeliveryTimeTo(),
      ),
    );

    return this.ptlDeliveryRange;
  }

  public onPtlPickupRangeChange(range: DateRange): void {
    if (!(this.quote instanceof PtlQuote)) {
      return;
    }

    const timeFrom = this.readRangeStartTime(range.getStart());
    const timeTo = this.readRangeEndTime(range.getEnd());

    this.quote.setIntendedPickupDate(range.getStart());
    this.quote.setIntendedPickupDateTo(range.getEnd());
    this.quote.setIntendedPickupTimeFrom(timeFrom);
    this.quote.setIntendedPickupTimeTo(timeTo);
    this.updateQuote({
      intendedPickupDate: range.getStart(),
      intendedPickupDateTo: range.getEnd(),
      intendedPickupTimeFrom: timeFrom,
      intendedPickupTimeTo: timeTo,
    });
  }

  public onPtlDeliveryRangeChange(range: DateRange): void {
    if (!(this.quote instanceof PtlQuote)) {
      return;
    }

    const timeFrom = this.readRangeStartTime(range.getStart());
    const timeTo = this.readRangeEndTime(range.getEnd());

    this.quote.setIntendedDeliveryDateFrom(range.getStart());
    this.quote.setIntendedDeliveryDateTo(range.getEnd());
    this.quote.setIntendedDeliveryTimeFrom(timeFrom);
    this.quote.setIntendedDeliveryTimeTo(timeTo);
    this.updateQuote({
      intendedDeliveryDateFrom: range.getStart(),
      intendedDeliveryDateTo: range.getEnd(),
      intendedDeliveryTimeFrom: timeFrom,
      intendedDeliveryTimeTo: timeTo,
    });
  }

  public isPtlPickupEnabled(): boolean {
    return this.getPtlPickupRange() !== null;
  }

  public onPtlPickupEnabledChange(enabled: boolean): void {
    if (!(this.quote instanceof PtlQuote)) {
      return;
    }

    const dates = enabled ? this.buildPtlPickupDefaults() : null;
    this.quote.setIntendedPickupDate(dates ? dates.getStart() : null);
    this.quote.setIntendedPickupDateTo(dates ? dates.getEnd() : null);
    this.quote.setIntendedPickupTimeFrom(null);
    this.quote.setIntendedPickupTimeTo(null);
    this.updateQuote({
      intendedPickupDate: dates ? dates.getStart() : null,
      intendedPickupDateTo: dates ? dates.getEnd() : null,
      intendedPickupTimeFrom: null,
      intendedPickupTimeTo: null,
    });
  }

  public isPtlDeliveryEnabled(): boolean {
    return this.getPtlDeliveryRange() !== null;
  }

  public onPtlDeliveryEnabledChange(enabled: boolean): void {
    if (!(this.quote instanceof PtlQuote)) {
      return;
    }

    const dates = enabled ? this.buildPtlDeliveryDefaults() : null;
    this.quote.setIntendedDeliveryDateFrom(dates ? dates.getStart() : null);
    this.quote.setIntendedDeliveryDateTo(dates ? dates.getEnd() : null);
    this.quote.setIntendedDeliveryTimeFrom(null);
    this.quote.setIntendedDeliveryTimeTo(null);
    this.updateQuote({
      intendedDeliveryDateFrom: dates ? dates.getStart() : null,
      intendedDeliveryDateTo: dates ? dates.getEnd() : null,
      intendedDeliveryTimeFrom: null,
      intendedDeliveryTimeTo: null,
    });
  }

  public getPtlPickupWarning(): string {
    if (!(this.quote instanceof PtlQuote)) {
      return null;
    }

    return this.startsAfter(
      this.quote.getIntendedPickupDate(),
      this.quote.getIntendedPickupTimeFrom(),
      this.quote.getIntendedPickupDateTo(),
      this.quote.getIntendedPickupTimeTo(),
    )
      ? "Pickup must not end before it starts"
      : null;
  }

  public getPtlDeliveryWarning(): string {
    if (!(this.quote instanceof PtlQuote)) {
      return null;
    }

    if (
      this.startsAfter(
        this.quote.getIntendedDeliveryDateFrom(),
        this.quote.getIntendedDeliveryTimeFrom(),
        this.quote.getIntendedDeliveryDateTo(),
        this.quote.getIntendedDeliveryTimeTo(),
      )
    ) {
      return "Delivery must not end before it starts";
    }

    if (
      this.startsAfter(
        this.quote.getIntendedPickupDate(),
        this.quote.getIntendedPickupTimeFrom(),
        this.quote.getIntendedDeliveryDateFrom(),
        this.quote.getIntendedDeliveryTimeFrom(),
      )
    ) {
      return "Delivery must not start before pickup";
    }

    return null;
  }

  public getQuoteValidityWarning(): string {
    if (!(this.quote instanceof PtlQuote)) {
      return null;
    }

    return this.startsAfter(
      this.quote.getValidFrom(),
      this.quote.getValidFromTime(),
      this.quote.getValidTo(),
      this.quote.getValidToTime(),
    )
      ? "Quote validity must not end before it starts"
      : null;
  }

  private selectPtlValidity(quote: PtlQuote, start: Date, end: Date): void {
    const validFromTime = this.readRangeStartTime(start);
    const validToTime = this.readRangeEndTime(end);

    quote.setValidFromTime(validFromTime);
    quote.setValidToTime(validToTime);
    this.selectedRange = new DateRange(start, end);

    this.updateQuote({
      validFrom: start,
      validTo: end,
      validFromTime: validFromTime,
      validToTime: validToTime,
    });
  }

  public onPiecesChange(event) {
    const strValue = event.target.value.replace(",", ".");
    let pieces = parseFloat(strValue);
    if (isNaN(pieces)) {
      pieces = 0;
    }
    if (this.quote instanceof BaseLclQuote && this.isInt(pieces)) {
      this.quote.setPieces(pieces);
      this.quote.setPackages([]);
      this.updateQuote({ pieces: pieces, packages: [] });
    }
  }

  public selectTab(tab: string): void {
    if (tab === this.activeTab) {
      return;
    }
    if (!this.isSendPossible()) {
      const warns = this.getTabSwitchWarnings();

      this.notifyService.toast(
        TOAST.WARN,
        "Not enough data",
        "Please select " + warns.join(", "),
      );

      // p-tabs owns the header value on click; revert it since the switch is vetoed.
      this.QuoteTabs()?.value.set(this.activeTab);

      return;
    }
    this.tabBehavior.next(tab);
  }

  public isSendPossible(): boolean {
    if (
      this.quote instanceof BaseStandardQuote &&
      this.quote.getHasDestination() &&
      this.quote.getHasOrigin()
    ) {
      return this.quote.isSendable() && this.haveRoute[this.quote.getId()];
    }

    return this.quote.isSendable();
  }

  public hasValidDate(): boolean {
    return (
      !this.quote.getId() ||
      this.quote.getStatus() === quoteStatus.sent ||
      this.quote.getStatus() === quoteStatus.accepted ||
      differenceInCalendarDays(this.quote.getValidFrom(), new Date()) >= 0
    );
  }

  public selectServiceCodes(event): void {
    if (this.quote instanceof AirQuote) {
      this.updateQuote({ serviceCodes: event });
    }
  }

  public selectEquipment(event): void {
    if (
      this.quote instanceof FclQuote ||
      this.quote instanceof FclTariff ||
      this.quote instanceof MultiQuote
    ) {
      if (event.length < 1 && !(this.quote instanceof MultiQuote)) {
        this.notifyService.toast(
          TOAST.INFO,
          "You must have at least one container type selected",
        );

        return;
      }
      this.childQuotes = [];

      this.updateQuote({
        equipment: event,
        equipmentQuantity:
          this.equipmentQuantityService.getEquipmentQuantityForEquipment(event),
      });
    }
  }

  public selectWeightBrackets(event): void {
    if (this.quote instanceof AirTariffQuote) {
      if (event.length < 1) {
        this.notifyService.toast(
          TOAST.INFO,
          "You must have at least one weight bracket type selected",
        );

        return;
      }

      this.updateQuote({
        weightBrackets: event,
      });
    }
  }

  public onQuoteTypeChange() {
    this.changeQuoteType(this.selectedQuoteType);
    this.user?.setLastQuoteType(this.selectedQuoteType);
  }

  public onLocationChange(event: {
    type: string;
    locations: QuoteLocation[];
  }): void {
    if (this.quote instanceof MultiQuote) {
      this.childQuotes = [];
      this.fetchChildQuotes();
    } else if (this.quote instanceof BaseStandardQuote) {
      if (event.type === quoteLocationType.origin) {
        this.quote.setOriginLocations(event.locations);
      } else {
        this.quote.setDestinationLocations(event.locations);
      }

      this.setLocationKindFilterFromQuoteLocationsOrSelectedQuoteType();
      this.setLocationKindFilterOptions();
      this.fetchFreightRatesOrLocationCharges();
    }
  }

  public onChipsLocationKindFilterChange(filter: string) {
    this.locationKindFilter = filter;
  }

  public onRouteDetect(event: {
    detected: boolean;
    count: number;
    quote: BaseStandardQuote;
    rates: Rate[];
  }) {
    this.haveRoute[event.quote.getId()] = event.detected;
    if (this.quote instanceof MultiQuote) {
      this.haveRoute[this.quote.getId()] = false;
      this.haveRoute.map((detected) => {
        if (detected) {
          this.haveRoute[this.quote.getId()] = true;
        }
      });
    }
    this.routeCount = event.count;
    this.selectedRates = event.rates;
    for (let i = 0; i < this.selectedRates.length; i++) {
      if (this.selectedRates[i].getDeepLink()) {
        this.deepLink =
          "/api/booking/?url=" + this.selectedRates[i].getDeepLink();
      }
    }
  }

  public getCurrencyOptionsVisibility(): boolean {
    return this.quote instanceof BaseStandardQuote;
  }

  public setSendButtonVisibility(event: boolean) {
    this.isMailFormValid = event;
  }

  public nextTab() {
    switch (this.activeTab) {
      case this.tabs.freight:
        this.selectTab(
          this.quote instanceof LtlQuote ||
            this.quote instanceof FtlQuote ||
            this.quote instanceof PtlQuote
            ? this.tabs.layout
            : this.tabs.charges,
        );
        break;
      case this.tabs.quote:
      case this.tabs.charges:
      case this.tabs.contracts:
        this.selectTab(this.tabs.layout);
        break;
      case this.tabs.layout:
        this.selectTab(this.tabs.email);
        break;
    }
  }

  public areIncoTermsVisible(): boolean {
    return (
      this.quote instanceof BaseStandardQuote &&
      !(this.quote instanceof LtlQuote) &&
      !(this.quote instanceof FtlQuote) &&
      !(this.quote instanceof PtlQuote) &&
      !(this.quote instanceof BrokerageQuote)
    );
  }

  public isQuoteDirectionVisible(): boolean {
    return (
      (this.quote instanceof BaseStandardQuote ||
        this.quote instanceof FclTariff) &&
      !(this.quote instanceof LtlQuote) &&
      !(this.quote instanceof FtlQuote) &&
      !(this.quote instanceof PtlQuote)
    );
  }

  public hasResolvedLocations(): boolean {
    return this.quote instanceof BaseStandardQuote
      ? this.quote.hasResolvedLocations()
      : true;
  }

  public isFreightTabVisible() {
    return (
      this.quote instanceof BaseStandardQuote &&
      this.quote.getHasOrigin() &&
      this.quote.getHasDestination()
    );
  }

  public isFclQuote() {
    return this.quote instanceof FclQuote;
  }

  public isBrokerageQuote() {
    return this.quote instanceof BrokerageQuote;
  }

  public isAirQuote() {
    return this.quote instanceof AirQuote;
  }

  public isAirTariffQuote() {
    return this.quote instanceof AirTariffQuote;
  }

  public isFclQuoteOrFclTariff(): boolean {
    return this.quote instanceof FclQuote || this.quote instanceof FclTariff;
  }

  public isMultiQuote(): boolean {
    return this.quote instanceof MultiQuote;
  }

  public isLtlQuote() {
    return this.quote instanceof LtlQuote;
  }

  public isFtlQuote() {
    return this.quote instanceof FtlQuote;
  }

  public isPtlQuote() {
    return this.quote instanceof PtlQuote;
  }

  public isBaseLclQuote() {
    return this.quote instanceof BaseLclQuote;
  }

  public isEmailQuote() {
    return this.quote instanceof EmailQuote;
  }

  public areLocationChipsVisible() {
    return this.quote instanceof BaseStandardQuote;
  }

  public isFirstTabActive(): boolean {
    return (
      (this.isFreightTabVisible() && this.activeTab === this.tabs.freight) ||
      (!this.isFreightTabVisible() && this.activeTab === this.tabs.charges) ||
      this.activeTab === this.tabs.contracts ||
      this.activeTab === this.tabs.quote
    );
  }

  private isChargesTabWithComments(): boolean {
    return (
      this.activeTab === this.tabs.charges &&
      [quoteType.fcl, quoteType.lcl, quoteType.air].includes(
        this.quote.getQuoteType(),
      )
    );
  }

  public getChipLocationInput(chipType: string): QuoteLocation[] {
    if (this.quote instanceof BaseStandardQuote) {
      return chipType === this.chip.origin
        ? this.quote.getOriginLocations()
        : this.quote.getDestinationLocations();
    }
  }

  public isCommentTabActive(): boolean {
    return this.isFirstTabActive() || this.isChargesTabWithComments();
  }

  public isInternalCommentVisible() {
    return this.isCommentTabActive();
  }

  public isQuotePreviewVisible() {
    return this.activeTab === this.tabs.layout;
  }

  public isOfferCommentVisible() {
    return (
      this.isCommentTabActive() &&
      (this.quote instanceof BaseStandardQuote ||
        this.quote instanceof FclTariff)
    );
  }

  public getComment() {
    if (
      this.quote instanceof BaseStandardQuote ||
      this.quote instanceof FclTariff
    ) {
      return this.quote.getComment();
    }

    return null;
  }

  private hasCommentContent(comment: string | null): boolean {
    if (!comment) {
      return false;
    }

    // DOMParser decodes entities and strips tags via textContent; the replace
    // removes zero-width and non-breaking spaces so a visually-empty comment
    // (e.g. "<p><br></p>", "<p>&nbsp;</p>") is treated as empty.
    const text = new DOMParser()
      .parseFromString(comment, "text/html")
      .body.textContent?.replace(/[\u200B-\u200D\uFEFF\u00A0]/g, "")
      .trim();

    return !!text && text.length > 0;
  }

  public toggleOfferComment() {
    if (!this.showOfferComment) {
      this.updateQuote({ comment: null });
    }
  }

  public toggleInternalNotes() {
    if (!this.showInternalNotes) {
      this.internalComment = "";
      this.internalCommentSubject.next("");
    }
  }

  public getFclEquipment() {
    if (this.quote instanceof FclQuote || this.quote instanceof MultiQuote) {
      const equipment = this.quote.getEquipment()
        ? this.quote.getEquipment()
        : [];
      return new SortEquipmentPipe().transform(equipment);
    }
  }

  public showFirstTab(): void {
    if (this.quote instanceof BaseStandardQuote) {
      if (this.isFreightTabVisible()) {
        this.tabBehavior.next(this.tabs.freight);
      } else {
        this.tabBehavior.next(this.tabs.charges);
      }
    }

    if (this.quote instanceof FclTariff) {
      this.tabBehavior.next(this.tabs.contracts);
    }

    if (this.quote instanceof ManualQuote) {
      this.tabBehavior.next(this.tabs.quote);
    }

    if (this.quote instanceof EmailQuote) {
      this.tabBehavior.next(this.tabs.email);
    }
  }

  public isNegotiatedQuote(): boolean {
    return this.quote.getDisplayId() &&
      this.quote.getDisplayId().indexOf("-") !== -1
      ? true
      : false;
  }

  private fetchChildQuotes(): void {
    if (
      this.quote.isValid() &&
      this.quote instanceof MultiQuote &&
      this.childQuotes.length == 0
    ) {
      this.subscription.add(
        this.quoteRepository.getChildQuotes(this.quote.getId()).subscribe({
          next: (childQuotes) => {
            this.childQuotes = childQuotes;
          },
          error: (error) => {
            this.notifyService.toast(
              TOAST.ERROR,
              <string>new BackendErrorPipe().transform(error, "string"),
            );
          },
        }),
      );
    }
  }

  private handleQuoteUpdate(updatedQuote: Quote, pending: any): void {
    if (pending instanceof Client || pending instanceof DateRange) {
      const MailEditor = this.MailEditor();
      if (MailEditor) {
        MailEditor.refreshPreview();
      }
    }

    if (pending["insurance"]) {
      this.quote = updatedQuote;
    }

    this.quote.setUpdatedBy(updatedQuote.getUpdatedBy());

    if (
      pending["clients"] &&
      this.quote instanceof BaseStandardQuote &&
      updatedQuote instanceof BaseStandardQuote
    ) {
      this.quote.setOfferLanguage(updatedQuote.getOfferLanguage());
    }

    // A new client invalidates the reply the email was built on, so the backend resets it.
    if (pending["clients"] && updatedQuote.getEmail()) {
      this.quote.setEmail(updatedQuote.getEmail());
    }

    if (pending["quoteType"]) {
      this.quote = updatedQuote;
      if (
        pending["quoteType"] === quoteType.ptl &&
        this.quote instanceof PtlQuote
      ) {
        this.quote.setIntendedPickupDate(
          pending["intendedPickupDate"] || new Date(),
        );
      }

      this.setLocationKindFilterOptions();

      if (updatedQuote instanceof FclTariff) {
        this.updateFclTariffComponents();
      }

      let tab = this.tabs.freight;
      if (pending["quoteType"] === quoteType.fclTariff) {
        tab = this.tabs.contracts;
      }
      if (pending["quoteType"] === quoteType.manual) {
        tab = this.tabs.quote;
      }
      if (pending["quoteType"] === quoteType.email) {
        tab = this.tabs.email;
      }
      this.tabBehavior.next(tab);

      // The new quote carries its own header values (incoterm, direction, validity);
      // without this the form keeps the selections left over from the previous type.
      this.refreshQuoteDetails();
    }

    // TODO: IMO we should always execute: "this.quote = this.updatedQuote;"
    if (
      updatedQuote instanceof BaseLclQuote &&
      this.quote instanceof BaseLclQuote
    ) {
      this.quote.setChargeableWeight(updatedQuote.getChargeableWeight());
    }

    if (
      updatedQuote instanceof BaseStandardQuote &&
      !Object.prototype.hasOwnProperty.call(pending, "comment") &&
      !Object.prototype.hasOwnProperty.call(pending, "internalComment")
    ) {
      this.fetchFreightRatesOrLocationCharges();
    }

    if (
      pending instanceof Client ||
      pending instanceof DateRange ||
      pending instanceof QuoteEmail ||
      Object.prototype.hasOwnProperty.call(pending, "customTerms") ||
      Object.prototype.hasOwnProperty.call(pending, "direction") ||
      Object.prototype.hasOwnProperty.call(pending, "incotermType") ||
      Object.prototype.hasOwnProperty.call(pending, "quoteType") ||
      Object.prototype.hasOwnProperty.call(pending, "showCarrier") ||
      Object.prototype.hasOwnProperty.call(pending, "equipment") ||
      Object.prototype.hasOwnProperty.call(pending, "showInland") ||
      Object.prototype.hasOwnProperty.call(pending, "showTotal") ||
      Object.prototype.hasOwnProperty.call(pending, "totalCurrency") ||
      Object.prototype.hasOwnProperty.call(pending, "showRateSurcharges") ||
      Object.prototype.hasOwnProperty.call(pending, "intendedPickupDate")
    ) {
      if (
        (updatedQuote instanceof BaseStandardQuote &&
          this.activeTab !== this.tabs.freight) ||
        pending instanceof DateRange ||
        Object.prototype.hasOwnProperty.call(pending, "equipment") ||
        pending instanceof Client
      ) {
        this.fetchFreightRatesOrLocationCharges();
      }
    }

    if (
      (Object.prototype.hasOwnProperty.call(pending, "weight") ||
        Object.prototype.hasOwnProperty.call(pending, "measurement")) &&
      this.quote instanceof BaseLclQuote &&
      updatedQuote instanceof BaseLclQuote
    ) {
      this.quote.setChargeableWeight(updatedQuote.getChargeableWeight());
    }

    if (
      Object.prototype.hasOwnProperty.call(pending, "weight") ||
      Object.prototype.hasOwnProperty.call(pending, "measurement") ||
      Object.prototype.hasOwnProperty.call(pending, "equipment") ||
      Object.prototype.hasOwnProperty.call(pending, "cbm") ||
      Object.prototype.hasOwnProperty.call(pending, "ldm") ||
      Object.prototype.hasOwnProperty.call(pending, "pieces") ||
      Object.prototype.hasOwnProperty.call(pending, "packages") ||
      Object.prototype.hasOwnProperty.call(pending, "direction") ||
      Object.prototype.hasOwnProperty.call(pending, "incoterms")
    ) {
      this.eventService.eventBus.emit({
        event: eventType.quoteAverageMarginUpdated,
        data: this.quote,
      });
    }
    this.fetchChildQuotes();
    this.updateSharedComponents(
      pending instanceof QuoteEmail,
      !!pending["clients"],
    );
  }

  private updateSharedComponents(emailEdit = false, clientsChanged = false) {
    const QuoteTerms = this.QuoteTerms();
    if (QuoteTerms && !QuoteTerms.editableTerms) {
      QuoteTerms.fetchTerms();
    }
    const MailEditor = this.MailEditor();
    if (MailEditor) {
      MailEditor.refreshAfterQuoteUpdate(emailEdit, clientsChanged);
    }
    const SurchargeTypeMultiselect = this.SurchargeTypeMultiselect();
    if (SurchargeTypeMultiselect) {
      SurchargeTypeMultiselect.refresh();
    }
  }

  // Children read `quote` through a signal input, which only receives the new
  // value once this view has been refreshed. Calling them straight away would
  // make them re-fetch against the previous quote.
  //
  // Render hooks run outside NgZone, so a request started here would resolve
  // without a change detection tick and leave the loading skeleton on screen
  // until some unrelated event triggers one. Re-enter the zone first.
  public fetchFreightRatesOrLocationCharges(keepRows = false) {
    afterNextRender(
      () => {
        this.ngZone.run(() => {
          for (const freightRate of this.FreightRateList()) {
            freightRate.fetchFreightRates(keepRows);
          }
          for (const surcharge of this.SurchargeList()) {
            surcharge.fetchLocationCharges();
          }
        });
      },
      { injector: this.injector },
    );
  }

  public onQuoteSurchargeSelectionStart(): void {
    if (this.isPtlQuote()) {
      return;
    }

    for (const freightRate of this.FreightRateList()) {
      freightRate.showRefreshingRows();
    }
  }

  public onQuoteSurchargeSelectionChange(): void {
    if (this.isPtlQuote()) {
      return;
    }

    // The rate groups cannot change here, only their prices - keep the rows on
    // screen so toggling a surcharge does not collapse the list.
    this.fetchFreightRatesOrLocationCharges(true);
  }

  private updateFclTariffComponents() {
    const TariffContractList = this.TariffContractList();
    if (TariffContractList) {
      TariffContractList.resetGridParamsOptions();
      TariffContractList.setGridParamsOptions();
      TariffContractList.buildTierList();
      TariffContractList.fetchRates();
    }
  }

  private refreshQuoteDetails() {
    if (
      this.quote instanceof BaseStandardQuote ||
      this.quote instanceof FclTariff
    ) {
      this.selectedDirection = this.quote.getDirection();
    }
    if (this.quote instanceof BaseStandardQuote) {
      this.selectedIncotermType = this.quote.getIncotermType();
    }
    this.selectedQuoteType = this.quote.getQuoteType();
    this.internalComment = this.quote.getInternalComment();
    this.showInternalNotes = !!this.internalComment;
    this.showOfferComment = this.hasCommentContent(this.getComment());
    this.selectedRange = new DateRange(
      this.withValidityTime(this.quote.getValidFrom(), this.getValidFromTime()),
      this.withValidityTime(this.quote.getValidTo(), this.getValidToTime()),
    );
  }

  private removeEmptyQuote() {
    if (!this.quote.isValid() && this.quote.getSource() === null) {
      this.quoteRepository.deleteQuote(this.quote.getId()).subscribe({
        next: (deletedQuote) => {
          this.eventService.eventBus.emit({
            event: eventType.quoteDeleted,
            data: this.quote,
          });
          this.notifyService.toast(
            TOAST.SUCCESS,
            "Empty quote has been deleted",
          );
        },
        error: (err) => {
          this.sentry.handleError(err, { quoteOnDelete: this.quote });
          this.notifyService.toast(TOAST.ERROR, "Error");
        },
      });
    }
  }

  private changeQuoteType(qt: string): void {
    const payload: any = {
      quoteType: qt,
    };
    const today = new Date();
    const twoWeeksFromNow = new Date(today);
    twoWeeksFromNow.setDate(today.getDate() + 14);

    if (qt === quoteType.ptl) {
      payload.validFrom = today;
      payload.validTo = today;
      payload.validFromTime = null;
      payload.validToTime = null;
      this.selectedRange = new DateRange(
        this.withValidityTime(today, this.defaultValidFromTime),
        this.withValidityTime(today, this.defaultValidToTime),
      );
    } else if (this.isPtlQuote() || this.isValidityOver()) {
      payload.validTo = twoWeeksFromNow;
      this.selectedRange = new DateRange(
        this.quote.getValidFrom() || today,
        twoWeeksFromNow,
      );
    }

    if (qt === quoteType.multi && this.quote.getClients().length > 1) {
      payload.clients = [];
    }
    this.updateQuote(payload);

    this.setLocationKindFilterFromQuoteLocationsOrSelectedQuoteType();
    this.setLocationKindFilterOptions();

    this.locationKindFilter = null;
    // TODO: lcl and air freight is not working, fix it.
  }

  private isValidityOver(): boolean {
    const validTo = this.quote.getValidTo();

    return !validTo || differenceInCalendarDays(validTo, new Date()) < 0;
  }

  private setLocationKindFilterFromQuoteLocationsOrSelectedQuoteType() {
    this.locationKindFilter =
      this.quote instanceof BaseStandardQuote ? this.quote.getKind() : null;

    if (
      this.selectedQuoteType === this.quoteType.air ||
      this.selectedQuoteType === this.quoteType.airtariff
    ) {
      this.locationKindFilter = locationKind.airport;
    }
  }

  private setLocationKindFilterOptions() {
    this.locationKindFilterOptions =
      this.locationKindService.buildLocationKindFilterOptions(
        this.quote.getQuoteType(),
        this.locationKindFilter,
      );
  }

  private buildIncotermsList() {
    this.incotermsListConst.map((key) => {
      this.incotermsList.push({
        label: key.toUpperCase(),
        value: key.toUpperCase(),
      });
    });
  }

  private buildDirectionList() {
    Object.keys(this.quoteDirection).forEach((key) => {
      this.directionList.push({
        label: key,
        value: this.quoteDirection[key],
      });
    });
  }

  private buildQuoteTypeList() {
    this.quoteTypeList = [];
    Object.keys(this.quoteType).forEach((key) => {
      this.quoteTypeList.push({
        label: key.toUpperCase(),
        value: this.quoteType[key],
      });
    });
  }

  private addInitSubscriptions() {
    this.subscription.add(
      this.eventService.eventBus.subscribe((event) => {
        if (event.event === eventType.quoteUpdating) {
          this.loading = true;
        } else if (event.event === eventType.quoteUpdated) {
          this.quote.setUpdatedAt(new Date());
          this.loading = false;
        }
      }),
    );

    this.subscription.add(
      this.tabBehavior.subscribe((tab) => {
        this.activeTab = tab;
        this.isMailFormValid = false;
      }),
    );

    this.subscription.add(
      this.internalCommentSubject
        .pipe(distinctUntilChanged(), debounceTime(1000))
        .subscribe((internalComment) => {
          this.quote.setInternalComment(internalComment);
          this.updateQuote({ internalComment: internalComment });
        }),
    );
  }

  private setUser() {
    this.userService.getUser().then((user) => {
      this.user = user;
      this.buildQuoteTypeList();
    }, noop);
  }

  private applyPtlValidityOnCreate(): void {
    if (
      !this.shouldApplyPtlValidityOnCreate ||
      !(this.quote instanceof PtlQuote)
    ) {
      return;
    }

    this.shouldApplyPtlValidityOnCreate = false;
    this.applyPtlValidityDefaults(this.quote);

    const payload = {
      discriminator: this.quote.getQuoteType(),
      validFrom: format(this.today, "yyyy-MM-dd"),
      validTo: format(this.today, "yyyy-MM-dd"),
      validFromTime: null,
      validToTime: null,
    };

    this.subscription.add(
      this.quoteRepository.patchQuote(this.quote.getId(), payload).subscribe({
        next: (updatedQuote) => {
          this.quote = updatedQuote;
          if (this.quote instanceof PtlQuote) {
            this.applyPtlValidityDefaults(this.quote);
          }
          this.refreshQuoteDetails();
        },
        error: (err) => {
          this.sentry.handleError(err, { updateQuotePayload: payload });
          this.notifyService.toast(TOAST.ERROR, "Error");
        },
      }),
    );
  }

  private buildPtlPickupDefaults(): DateRange {
    const tomorrow = addDays(this.today, 1);

    return new DateRange(tomorrow, addDays(tomorrow, 1));
  }

  private buildPtlDeliveryDefaults(): DateRange {
    return new DateRange(addDays(this.today, 2), addDays(this.today, 3));
  }

  private applyPtlValidityDefaults(quote: PtlQuote): void {
    quote.setValidFrom(this.today);
    quote.setValidTo(this.today);
    quote.setValidFromTime(null);
    quote.setValidToTime(null);
    this.selectedRange = new DateRange(
      this.withValidityTime(this.today, this.defaultValidFromTime),
      this.withValidityTime(this.today, this.defaultValidToTime),
    );
  }

  private getValidFromTime(): string {
    if (!(this.quote instanceof PtlQuote)) {
      return null;
    }

    return this.quote.getValidFromTime() || this.defaultValidFromTime;
  }

  private getValidToTime(): string {
    if (!(this.quote instanceof PtlQuote)) {
      return null;
    }

    return this.quote.getValidToTime() || this.defaultValidToTime;
  }

  private withValidityTime(date: Date, time: string): Date {
    if (!date || !time) {
      return date;
    }

    const [hours, minutes] = time.split(":");
    const stamped = new Date(date);
    stamped.setHours(Number(hours), Number(minutes), 0, 0);

    return stamped;
  }

  // A null time is what the date picker reads back as "no time chosen": the
  // start of a range sits at midnight, its end one minute before the next one.
  private withRangeStartTime(date: Date, time: string): Date {
    return this.withRangeTime(date, time, 0, 0);
  }

  private withRangeEndTime(date: Date, time: string): Date {
    return this.withRangeTime(date, time, 23, 59);
  }

  private withRangeTime(
    date: Date,
    time: string,
    fallbackHours: number,
    fallbackMinutes: number,
  ): Date {
    if (!date) {
      return date;
    }

    const [hours, minutes] = time ? time.split(":") : [];
    const stamped = new Date(date);
    stamped.setHours(
      time ? Number(hours) : fallbackHours,
      time ? Number(minutes) : fallbackMinutes,
      0,
      0,
    );

    return stamped;
  }

  private readRangeStartTime(date: Date): string {
    return this.readRangeTime(date, 0, 0);
  }

  private readRangeEndTime(date: Date): string {
    return this.readRangeTime(date, 23, 59);
  }

  private readRangeTime(
    date: Date,
    boundaryHours: number,
    boundaryMinutes: number,
  ): string {
    if (
      !date ||
      (date.getHours() === boundaryHours &&
        date.getMinutes() === boundaryMinutes)
    ) {
      return null;
    }

    return format(date, "HH:mm");
  }

  // A side carrying no time spans the whole day, so on a shared date neither of
  // the two sits after the other - the rule the backend validates with.
  private startsAfter(
    date: Date,
    time: string,
    otherDate: Date,
    otherTime: string,
  ): boolean {
    if (!date || !otherDate) {
      return false;
    }

    const day = format(date, "yyyy-MM-dd");
    const otherDay = format(otherDate, "yyyy-MM-dd");

    if (day !== otherDay) {
      return day > otherDay;
    }

    return !!time && !!otherTime && time > otherTime;
  }

  // The pickers keep the object identity of their bound value, so rebuild only on a real change.
  private buildStableRange(
    current: DateRange,
    from: Date,
    to: Date,
  ): DateRange {
    if (!from || !to) {
      return null;
    }

    if (
      current &&
      current.getStart().getTime() === from.getTime() &&
      current.getEnd().getTime() === to.getTime()
    ) {
      return current;
    }

    return new DateRange(from, to);
  }

  private emitQuoteUpdating(): void {
    this.eventService.eventBus.emit({
      event: eventType.quoteUpdating,
      data: this.quote,
    });
  }

  private emitQuoteUpdated(): void {
    this.eventService.eventBus.emit({
      event: eventType.quoteUpdated,
      data: this.quote,
    });
  }

  private refreshAverageMargin(id = this.quote.getId()) {
    if (this.quote.getStatus() !== quoteStatus.draft) {
      return;
    }

    if (
      this.quote instanceof ManualQuote ||
      this.quote instanceof AirTariffQuote ||
      this.quote instanceof FclTariff ||
      this.quote instanceof MultiQuote
    ) {
      return;
    }

    this.subscription.add(
      this.quoteRepository.refreshAverageMargin(id).subscribe({
        next: (updatedQuote) => {
          this.quote.setAverageMargin(updatedQuote.getAverageMargin());
          if (
            this.quote instanceof BaseLclQuote &&
            updatedQuote instanceof BaseLclQuote
          ) {
            this.quote.setAverageMarginPercent(
              updatedQuote.getAverageMarginPercent(),
            );
          }
          if (
            this.quote instanceof LtlQuote &&
            updatedQuote instanceof LtlQuote
          ) {
            this.quote.setAverageMarginEuro(
              updatedQuote.getAverageMarginEuro(),
            );
          }
        },
        error: (error) => {
          this.notifyService.toast(
            TOAST.ERROR,
            <string>new BackendErrorPipe().transform(error, "string"),
          );
        },
      }),
    );
  }

  private watchEventsToRefreshMargin() {
    this.marginUpdateSubject.pipe(debounceTime(10000)).subscribe((item) => {
      this.refreshAverageMargin();
    });
  }

  private watchEventsToRefreshQuote() {
    this.subscription.add(
      this.eventService.eventBus.subscribe((event: { event: string }) => {
        if (event.event === eventType.quoteAverageMarginUpdated) {
          this.marginUpdateSubject.next(null);
          this.updateSharedComponents();
        }
      }),
    );
  }

  // TODO: Move to a service
  private getTabSwitchWarnings(): string[] {
    const warns = [];

    if (!this.quote.getClients() || !this.quote.getClients().length) {
      warns.push("client");
    }

    if (this.isEquipmentRequired() && !this.hasEquipment()) {
      warns.push("equipment");
    }

    if (this.quote instanceof FclTariff) {
      if (
        !this.quote.getRates() ||
        (this.quote.getRates() && this.quote.getRates().length === 0)
      ) {
        warns.push("at least one contract");
      }
    }

    if (this.quote instanceof BaseStandardQuote) {
      if (!this.haveRoute[this.quote.getId()]) {
        warns.push("at least one rate");
      }
      if (
        this.quote.getOriginLocations() &&
        this.quote.getOriginLocations().length === 0
      ) {
        warns.push("at least one origin location");
      }
      if (
        this.quote.getDestinationLocations() &&
        this.quote.getDestinationLocations().length === 0
      ) {
        warns.push("at least one destination location");
      }
    }

    if (this.quote instanceof BaseLclQuote) {
      if (!this.quote.getWeight()) {
        warns.push("quote weight");
      }
      if (this.quote instanceof PtlQuote) {
        if (!this.quote.getLdm()) {
          warns.push("quote ldm");
        }
      } else if (!this.quote.getMeasurement()) {
        warns.push("quote measurement");
      }
    }

    return warns;
  }

  private setQuoteFromResolve() {
    if (this.getQuoteResolve()) {
      this.redirectToNewQuote(this.getQuoteResolve());
      this.applyPtlValidityOnCreate();
      this.showFirstTab();
    }
  }

  private getQuoteResolve(): Quote {
    return this.route.data["value"]["quote"];
  }

  private scrollTop() {
    document.body.scrollTop = document.documentElement.scrollTop = 0;
  }

  private observeTermsChanges() {
    this.subscription.add(
      this.termsSubject.pipe(distinctUntilChanged()).subscribe((newTerms) => {
        this.updateQuote({ customTerms: newTerms });
      }),
    );
  }
}
